//@ts-nocheck
/**
 * src/routes/sections/userRoutes.tsx
 * ---------------------------------------------------------------------------
 * User / Public section routes.
 * Includes the public landing, sector explorer, projects explorer,
 * and the Unity WebGL immersive viewer.
 *
 * Also nests the admin section inside GuestGuard (matching the original structure).
 *
 * Extracted from the monolithic routes.tsx for domain separation.
 * All route paths and components are preserved exactly.
 *
 * IMPORTANT: scene/:teleportationId is intentionally OUTSIDE the UserLayout
 * wrapper so that it renders inside the dedicated ImmersiveLayout (fullscreen,
 * no navbar, no footer, no website chrome).
 */
import React from 'react';
import { paths } from '../paths';

// Layouts & Guards
import UserLayout from '../../layouts/UserLayout';
import ImmersiveLayout from '../../layouts/ImmersiveLayout';
import { GuestGuard, AuthGuard } from '../../guard/AuthGuard';

// Public pages (real files unchanged, imported via existing index adapters)
import SectorLayout from '../../Pages/Sector';
import ProjectsExplorer from '../../Pages/ProjectsExplorer';
import ViewUnity from '../../Pages/ViewUnity';

// Utility / shared components
import Modal from '../../Components/Modal/Modal';
import NotFound from '../../Components/404 Not Found/NotFound';
import Unauthorized from '../../Components/Unauthorized/Unauthorized';

// Legal pages
import PrivacyPolicy from '../../Pages/Legal/PrivacyPolicy';
import TermsConditions from '../../Pages/Legal/TermsConditions';

// Admin section (nested inside GuestGuard, matching original routes.tsx structure)
import { adminRoutes } from './adminRoutes';

// Resources / Documentation (public, no auth required)
import ResourcesLayout from '../../Pages/Resources/ResourcesLayout';
import ResourcesPage from '../../Pages/Resources/ResourcesPage';
import TourEditorPage from '../../Pages/Tours/TourEditorPage';
import WebUrlView from '../../modules/webUrls/pages/WebUrlView/WebUrlView';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

/**
 * Builds user/public route config.
 * Admin routes are nested inside GuestGuard (original structure preserved).
 *
 * @param {object} options
 * @param {boolean} options.immersive         - Hides sector routes when true
 * @param {boolean} options.browseWithoutLogin - Allows unauthenticated browsing
 * @param {any}     options.user              - Logged-in user for admin AuthGuard
 * @param {boolean} options.isLoading         - Loading state for AdminLayout
 */
export const userRoutes = ({ immersive, browseWithoutLogin, isConfigLoading, user, isLoading }) => [
  {
    path: paths.public.root,
    element: <UserLayout user={user} />,
    children: [
      // ── Protected User Routes ──
      {
        element: <GuestGuard browseWithoutLogin={browseWithoutLogin} isConfigLoading={isConfigLoading} />,
        children: [
          ...(!immersive ? [{ path: 'sector/:slug', element: <SectorLayout /> }] : []),
          { path: 'projects', element: <ProjectsExplorer /> },
        ],
      },
    ],
  },
  // ── Protected Immersive Routes ──
  {
    element: <GuestGuard browseWithoutLogin={browseWithoutLogin} isConfigLoading={isConfigLoading} />,
    children: [
      { path: 'scene/:teleportationId', element: <ImmersiveLayout /> },
      { path: 'unity-scene/:id', element: <ViewUnity /> },
    ],
  },
  // ── Standalone 360 tour editor (fullscreen, outside CMS shell) ──
  {
    element: <GuestGuard browseWithoutLogin={false} />,
    children: [
      {
        path: paths.admin.tours.create.slice(1),
        element: (
          <AuthGuard loggedInUser={user}>
            <TourEditorPage />
          </AuthGuard>
        ),
      },
      {
        path: paths.admin.webUrls.view(':id').slice(1),
        element: (
          <AuthGuard loggedInUser={user}>
            <WebUrlView />
          </AuthGuard>
        ),
      },
    ],
  },
  // ── Admin / CMS routes ──
  {
    element: <GuestGuard browseWithoutLogin={false} />,
    children: [...adminRoutes({ user, isLoading })],
  },

  // ── Resources / Documentation (public, no auth) ────────────────────────
  {
    path: 'resources',
    element: <ResourcesLayout user={user} />,
    children: [
      { index: true, element: <ResourcesPage section="overview" /> },
      { path: 'interactive-3d', element: <ResourcesPage section="interactive-3d" /> },
      { path: 'ar-experiences', element: <ResourcesPage section="ar-experiences" /> },
      { path: '360-tours', element: <ResourcesPage section="360-tours" /> },
      { path: 'access-control', element: <ResourcesPage section="access-control" /> },
      { path: 'publish-controls', element: <ResourcesPage section="publish-controls" /> },
      { path: 'instant-updates', element: <ResourcesPage section="instant-updates" /> },
    ],
  },

  // ── Standalone utility routes ───────────────────────────────────────────
  {
    path: paths.modal,
    element: (
      <Modal
        icon={<FontAwesomeIcon icon={faCircleExclamation} />}
        title={'Are you sure?'}
        subtitle={'You wont be able to revert this!'}
      />
    ),
  },
  { path: paths.page404, element: <NotFound /> },
  { path: paths.unauthorized, element: <Unauthorized /> },
  // ── Legal pages (public, no auth required) ─────────────────────────────
  { path: paths.public.privacyPolicy, element: <PrivacyPolicy /> },
  { path: paths.public.termsConditions, element: <TermsConditions /> },
];
